import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Sun, Moon, Play, X, Pause, Shield, ShieldCheck, Smartphone, Key, Copy, Eye, EyeOff, AlertTriangle, CheckCircle, Phone, PhoneForwarded, Loader2, Columns2, Maximize2, Mail, Lock, Clock } from 'lucide-react';
import AvailabilitySection from '../components/AvailabilitySection';
import { ChevronDown, ChevronUp, Lock as LockIcon } from 'lucide-react';
import { usePageHeader } from '../context/PageHeaderContext';


// Admin self-rotation cool-off panel.
//
// Server-authoritative countdown — we never trust a client-side
// setTimeout for the unlock moment. The doc stores `auto_approve_at`
// as an absolute ISO timestamp; we render time-remaining off that.
// Navigation away / browser close / app reopen all show the SAME
// remaining time when the user returns because the timestamp is
// fixed on the server.
//
// While the cool-off is live we poll `/mfa/recovery-key/status`
// every 5s for two reasons: (a) catch a peer admin clicking
// "Restrict & Force Logout" (status flips to "blocked" → we toast
// + return user to initial step), (b) catch the sweeper's
// auto-approval at T=5min (status flips to "approved" with a
// recovery_key plaintext — we hand it up to the parent).
const AdminCooloffPanel = ({ request, api, onCleared, onRevealReady }) => {
  const [now, setNow] = useState(() => Date.now());
  const [currentReq, setCurrentReq] = useState(request);
  // Local 1s tick for the countdown display (purely visual).
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Server poll for status / approval / block transitions.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get('/mfa/recovery-key/status');
        if (cancelled) return;
        const req = r.data?.request;
        if (req) setCurrentReq(req);
        if (r.data?.recovery_key) {
          // Sweeper approved → reveal moves to parent.
          onRevealReady && onRevealReady(r.data.recovery_key);
          return;
        }
        if (req && req.status === 'blocked') {
          toast.error(`Your rotation was blocked by ${req.blocked_by_name || 'another admin'}.`);
          onCleared && onCleared();
          return;
        }
        if (req && (req.status === 'expired' || req.status === 'cancelled' || req.status === 'rejected')) {
          onCleared && onCleared();
          return;
        }
      } catch (_e) { /* keep polling */ }
    };
    const id = setInterval(tick, 5000);
    tick();
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoApproveMs = currentReq?.auto_approve_at ? new Date(currentReq.auto_approve_at).getTime() : 0;
  const remainMs = Math.max(0, autoApproveMs - now);
  const mm = Math.floor(remainMs / 60000);
  const ss = Math.floor((remainMs % 60000) / 1000).toString().padStart(2, '0');
  const peers = currentReq?.approver_count || 0;

  const handleCancel = async () => {
    try {
      await api.post('/mfa/recovery-key/cancel');
      toast.success('Rotation cancelled.');
      onCleared && onCleared();
    } catch (e) {
      toast.error('Could not cancel');
    }
  };

  return (
    <div className="bg-muted/30 rounded-lg p-4 space-y-3" data-testid="recovery-key-admin-cooloff">
      <div className="flex items-center gap-2">
        <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
        <p className="font-semibold">High-level admin action — please wait</p>
      </div>
      <p className="text-sm text-muted-foreground">
        This is a high-level admin action. Please wait <strong>5 minutes</strong> while we confirm
        with the other Admins ({peers} {peers === 1 ? 'admin' : 'admins'} notified).
        Any of them can stop this rotation by clicking Restrict & Force Logout on their
        notification. If no one objects, your new recovery key will be available to reveal at the end of the timer.
      </p>
      <div className="flex items-center justify-center py-4">
        <div className="text-5xl font-mono font-bold text-amber-700 dark:text-amber-400" data-testid="recovery-key-cooloff-timer">
          {mm}:{ss}
        </div>
      </div>
      <p className="text-xs text-muted-foreground text-center">
        You can navigate away from this page — the timer keeps running on the server.
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={handleCancel}
          data-testid="recovery-key-admin-cancel"
        >
          Cancel my rotation
        </Button>
      </div>
    </div>
  );
};


// Welcome Video Player
const WelcomeVideoPlayer = ({ onClose }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      if (videoRef.current.currentTime > maxWatchedTime) {
        setMaxWatchedTime(videoRef.current.currentTime);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeeking = () => {
    if (videoRef.current && videoRef.current.currentTime > maxWatchedTime + 0.5) {
      videoRef.current.currentTime = maxWatchedTime;
      toast.warning('Please watch the video without skipping');
    }
  };

  const remainingTime = Math.max(0, duration - currentTime);

  return (
    <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center">
      <div className="relative w-full max-w-md mx-4">
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <Button
            variant="ghost"
            className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
            onClick={handlePause}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Countdown Timer */}
        {duration > 0 && (
          <div className="absolute top-4 left-4 z-10 bg-black/60 px-3 py-1 rounded-full">
            <span className="text-white text-sm font-medium">
              {formatTime(remainingTime)} remaining
            </span>
          </div>
        )}
        
        <video
          ref={videoRef}
          className="w-full rounded-lg"
          autoPlay
          playsInline
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onSeeking={handleSeeking}
          onEnded={onClose}
        >
          <source src="/intro_video.mp4" type="video/mp4" />
        </video>
      </div>
    </div>
  );
};

// ────── Default Call Recording card (iter156) ──────
// Admin + special users only. Lets an admin choose whether incoming
// and/or outgoing PSTN calls are recorded BY DEFAULT for their own
// user. Toggling OFF means:
//   • Outgoing: keypad toggle on /calls starts OFF (no recording, no
//     call-start disclaimer, no AI transcript/summary). Admin can flip
//     ON before dialing for a single call.
//   • Incoming: IVR digit-3 handler in `phone_routing.py` checks the
//     target user's `default_record_incoming` and routes via the simple
//     <Dial><Client/></Dial> path with no recording attrs and no
//     disclaimer announcement when set to False.
// The inverse holds when toggles are ON: keypad starts ON, admin can
// flip OFF for one call, and recording resets back to ON on the next.
const DefaultCallRecordingCard = ({ api }) => {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(null); // 'incoming' | 'outgoing' | null
  const [out, setOut] = useState(true);
  const [inc, setInc] = useState(true);

  useEffect(() => {
    if (!open || loaded) return;
    (async () => {
      try {
        const r = await api.get('/users/me/call-recording-defaults');
        setOut(!!r.data?.default_record_outgoing);
        setInc(!!r.data?.default_record_incoming);
      } catch (e) {
        // Leave defaults true; nothing actionable here.
      } finally {
        setLoaded(true);
      }
    })();
  }, [open, loaded, api]);

  const save = async (key, value) => {
    setSaving(key === 'default_record_outgoing' ? 'outgoing' : 'incoming');
    try {
      await api.put('/users/me/call-recording-defaults', { [key]: value });
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
      // Revert local state on failure
      if (key === 'default_record_outgoing') setOut((prev) => !value);
      else setInc((prev) => !value);
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card data-testid="default-call-recording-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-4 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]"
        data-testid="default-call-recording-card-toggle"
      >
        <Phone className="w-5 h-5 text-[#586D52]" />
        <div className="flex-1 min-w-0">
          <CardTitle className="text-base">Default Call Recording</CardTitle>
          <CardDescription className="text-xs">
            Set per-user defaults for inbound and outbound external calls.
          </CardDescription>
        </div>
        {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {open && (
        <CardContent className="pt-0 space-y-3" data-testid="default-call-recording-body">
          <p className="text-xs text-muted-foreground -mt-1">
            When a default is OFF, neither party hears the recording disclaimer and the
            call is not recorded. You can still flip the keypad toggle for a single
            outgoing call.
          </p>
          <div className="flex items-center justify-between border-t pt-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="default-incoming-record" className="text-sm">
                Incoming External Calls
              </Label>
            </div>
            <Switch
              id="default-incoming-record"
              checked={inc}
              disabled={saving === 'incoming'}
              onCheckedChange={(v) => {
                setInc(v);
                save('default_record_incoming', v);
              }}
              data-testid="default-record-incoming-switch"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="default-outgoing-record" className="text-sm">
                Outgoing External Calls
              </Label>
            </div>
            <Switch
              id="default-outgoing-record"
              checked={out}
              disabled={saving === 'outgoing'}
              onCheckedChange={(v) => {
                setOut(v);
                save('default_record_outgoing', v);
              }}
              data-testid="default-record-outgoing-switch"
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
};


// ────── Email Signature card (iter133) ──────
// Collapsed by default. On expand, fetches the user's signature
// HTML from `/me/email-signature` (server-generated by
// email_inbox._build_signature_html — the exact same function used
// at send time) and offers a 1-click "Copy HTML" button.
const EmailSignatureCard = ({ api }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);          // preview data (basic compat = what's rendered)
  const [fullHtml, setFullHtml] = useState('');    // pre-fetched compat=full HTML for copy
  const [outlookHtml, setOutlookHtml] = useState(''); // pre-fetched compat=outlook HTML for Provider button
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);   // null | 'full' | 'basic' | 'outlook'
  const [alignment, setAlignment] = useState('left'); // 'left' | 'center' | 'right'
  const [previewTheme, setPreviewTheme] = useState('light'); // 'light' | 'dark'

  // iter175e — Pre-fetch BOTH the basic-compat and full-compat HTML
  // when the card opens or alignment/theme changes. Caching them in
  // state means the Copy button handlers can call
  // `navigator.clipboard.write()` SYNCHRONOUSLY after the click —
  // preserving the user-gesture context that the Clipboard API
  // requires. Previously the copy handler awaited an `api.get()`
  // first, which broke the user-gesture chain and caused browsers
  // (Safari, modern Chrome) to silently reject the clipboard write.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    let cancelled = false;
    // iter175ae — Fetch the basic and full variants INDEPENDENTLY
    // (was Promise.all → either failure wiped both). Now if the
    // server returns an error on one variant, the other still
    // populates state and that button still works.
    const fetchOne = async (compat, setter) => {
      try {
        const res = await api.get('/me/email-signature', { params: { alignment, theme: previewTheme, compat } });
        if (cancelled) return;
        setter(res && res.data);
      } catch (e) {
        if (cancelled) return;
        // Surface the failure so the user knows why the button is disabled.
        console.warn(`[email-signature] prefetch ${compat} failed:`, e);
      }
    };
    Promise.allSettled([
      fetchOne('basic',   (d) => setData(d || { html: '', text: '', is_onboarded: false })),
      fetchOne('full',    (d) => setFullHtml((d && d.html) || '')),
      fetchOne('outlook', (d) => setOutlookHtml((d && d.html) || '')),
    ]).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, alignment, previewTheme, api]);

  // iter175e — SYNCHRONOUS clipboard write. The Clipboard API
  // requires the call to happen within the user-gesture window —
  // an `await` before `clipboard.write()` breaks that and the
  // browser silently rejects the write (no error thrown). So we
  // pre-fetched the HTML in the useEffect above and read it from
  // state here. No network round-trip between click and copy.
  const copyHtml = (compat /* 'full' | 'basic' | 'outlook' */) => {
    let htmlForCopy;
    if (compat === 'basic') htmlForCopy = data && data.html;
    else if (compat === 'outlook') htmlForCopy = outlookHtml;
    else htmlForCopy = fullHtml;
    if (!htmlForCopy) {
      toast.error('Signature is still loading — try again in a second.');
      return;
    }
    // iter175ae — Reverted iter175ad: execCommand's `innerHTML` step
    // strips `<style>` blocks AND `<!--[if mso]>` conditional
    // comments (both treated as invalid in non-<head> contexts by
    // browsers' HTML parser), so the contenteditable path is
    // STRICTLY WORSE than ClipboardItem for the Provider variant.
    // Going back to ClipboardItem with a robust onFailure fallback.
    let writePromise = null;
    if (window.ClipboardItem && navigator.clipboard?.write) {
      try {
        writePromise = navigator.clipboard.write([
          new window.ClipboardItem({
            'text/html': new Blob([htmlForCopy], { type: 'text/html' }),
            'text/plain': new Blob([htmlForCopy], { type: 'text/plain' }),
          }),
        ]);
      } catch (e) { writePromise = null; }
    }
    // FALLBACK: contenteditable + execCommand('copy'). Runs SYNC
    // so user-gesture is preserved either way.
    const fallbackExecCopy = () => {
      try {
        const sandbox = document.createElement('div');
        sandbox.setAttribute('contenteditable', 'true');
        sandbox.style.position = 'fixed';
        sandbox.style.left = '-9999px';
        sandbox.style.top = '0';
        sandbox.style.opacity = '0';
        sandbox.innerHTML = htmlForCopy;
        document.body.appendChild(sandbox);
        const range = document.createRange();
        range.selectNodeContents(sandbox);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        const ok = document.execCommand('copy');
        sel.removeAllRanges();
        document.body.removeChild(sandbox);
        return ok;
      } catch (_e) { return false; }
    };
    const onSuccess = () => {
      setCopied(compat);
      toast.success(
        compat === 'basic'
          ? 'Email Template signature copied (paste-safe for Wix / Mailchimp / template editors).'
          : compat === 'outlook'
            ? 'Email Provider signature copied (optimised for Outlook / Gmail / Apple Mail).'
            : `Email Provider signature copied (${previewTheme} theme, Outlook desktop rounded corners embedded).`
      );
      setTimeout(() => setCopied(null), 2200);
    };
    const onFailure = (err) => {
      // Last-ditch: try execCommand fallback inside the catch — but
      // by this point the user-gesture window may be closed and
      // execCommand will likely fail too. Still cheap to try.
      if (fallbackExecCopy()) { onSuccess(); return; }
      toast.error('Could not access clipboard. Check browser permissions: chrome://settings/content/clipboard');
      console.warn('[email-signature copy] clipboard.write failed:', err);
    };
    if (writePromise) {
      writePromise.then(onSuccess).catch(onFailure);
    } else {
      // ClipboardItem not supported at all — go straight to fallback.
      if (fallbackExecCopy()) { onSuccess(); }
      else { onFailure(new Error('ClipboardItem not supported and execCommand failed')); }
    }
  };

  return (
    <Card data-testid="email-signature-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-4 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]"
        data-testid="email-signature-card-toggle"
      >
        <Mail className="w-5 h-5 text-[#586D52]" />
        <div className="flex-1 min-w-0">
          <CardTitle className="text-base">Email Signature</CardTitle>
          <CardDescription className="text-xs">
            Paste your live ORB Uplift signature into Outlook, Gmail, or anywhere else.
          </CardDescription>
        </div>
        {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {open && (
        <CardContent className="space-y-3 pt-0">
          {/* First-load skeleton only — once we have data, we keep
              the preview mounted across alignment-tab refetches so
              the layout doesn't jump when the user switches tabs. */}
          {loading && !data && (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading your signature…
            </p>
          )}
          {data && !data.is_onboarded && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-900 dark:text-amber-200">
              Your signature unlocks once your onboarding is complete. Finish onboarding to enable copy.
            </div>
          )}
          {data?.is_onboarded && (
            <>
              {/* Alignment tabs — switching re-fetches the signature
                  so the live preview AND the Copy HTML output match
                  exactly. Left & Right include 15px breathing room
                  from the corresponding inbox edge; Center has no
                  inset.
                  iter175 — Light/Dark preview toggle sits to the
                  RIGHT of "Right Aligned". The toggle only controls
                  what the preview looks like; the Copy HTML payload
                  ALWAYS embeds both palettes via @media. */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <div className="inline-flex rounded-full bg-gray-100 dark:bg-[#1a1a1a] p-1" data-testid="email-sig-alignment-tabs">
                  {[
                    { key: 'left',   label: 'Left Aligned' },
                    { key: 'center', label: 'Centered' },
                    { key: 'right',  label: 'Right Aligned' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setAlignment(key)}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        alignment === key
                          ? 'bg-[#586D52] text-white shadow'
                          : 'text-gray-600 dark:text-gray-300 hover:text-foreground'
                      }`}
                      data-testid={`email-sig-align-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewTheme((t) => (t === 'light' ? 'dark' : 'light'))}
                  className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-[#1a1a1a] p-1 pr-3 pl-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-[#222] transition-colors"
                  data-testid="email-sig-theme-toggle"
                  aria-label={`Preview as ${previewTheme === 'light' ? 'dark' : 'light'} mode`}
                  title={`Currently previewing ${previewTheme} mode — click to switch`}
                >
                  <span className={`flex items-center justify-center w-6 h-6 rounded-full ${previewTheme === 'light' ? 'bg-white text-amber-500 shadow' : 'bg-[#1a1a1a] text-slate-300'}`}>
                    {previewTheme === 'light' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                  </span>
                  <span className="font-medium capitalize">{previewTheme}</span>
                </button>
              </div>
              {/* Live preview rendered straight from the server HTML so
                  WYSIWYG against what recipients actually receive.
                  Subtle opacity fade while refetching so the user has
                  a hint that the preview is updating without the
                  preview itself unmounting & re-flowing the page.
                  iter175 — `overflow-x:auto` lets the 408px-wide
                  card scroll horizontally on narrow mobile/tablet
                  viewports instead of bleeding past the Settings
                  card edge. The preview background mirrors the
                  selected previewTheme so light cards sit on a white
                  surface and dark cards on a near-black surface
                  (matches a recipient's inbox in either mode). */}
              <div
                className={`rounded-md border border-gray-200 dark:border-white/10 p-2 overflow-x-auto transition-opacity duration-150 ${loading ? 'opacity-60' : 'opacity-100'} ${previewTheme === 'dark' ? 'bg-[#111]' : 'bg-white'}`}
                dangerouslySetInnerHTML={{ __html: fullHtml || data.html }}
                data-testid="email-signature-live-preview"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="email-signature-copy-row">
                <Button
                  onClick={() => copyHtml('outlook')}
                  className="bg-[#586D52] hover:bg-[#4a5d46] text-white"
                  data-testid="email-signature-copy-provider-btn"
                  title="Optimised for Outlook desktop — locks photo dimensions via VML so high-DPI displays don't rescale it."
                >
                  {copied === 'outlook' ? (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Copied!</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-2" /> Email Provider</>
                  )}
                </Button>
                <Button
                  onClick={() => copyHtml('basic')}
                  variant="outline"
                  className="border-[#586D52] text-[#586D52] hover:bg-[#586D52]/10 dark:text-[#9BAF92] dark:border-[#9BAF92] dark:hover:bg-[#9BAF92]/10"
                  data-testid="email-signature-copy-template-btn"
                  title="Pure inline-styled tables — paste-safe in Wix, Mailchimp, Constant Contact, and other template editors."
                >
                  {copied === 'basic' ? (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Copied!</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-2" /> Email Template</>
                  )}
                </Button>
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  When to use each button + how to paste
                </summary>
                <div className="mt-2 space-y-2 text-muted-foreground">
                  <p><strong>Email Provider</strong> — for pasting into Outlook / Gmail / Apple Mail compose or signature settings. Uses whichever theme (Light / Dark) you've previewed above.</p>
                  <p><strong>Email Template</strong> — for pasting into Wix, Mailchimp, Constant Contact, or any template editor that displays raw markup as text. Uses whichever theme (Light / Dark) you've previewed above.</p>
                  <p><strong>Outlook (web/desktop):</strong> Settings → Mail → Compose and reply → in the Signature box, click the "View HTML" toggle (or paste directly).</p>
                  <p><strong>Gmail:</strong> Settings ⚙ → See all settings → General → Signature → paste into the rich-text box.</p>
                  <p><strong>Apple Mail:</strong> Mail → Settings → Signatures → drag the rendered preview directly, or paste the HTML.</p>
                  <p className="italic">The signature renders identically to what Uplift sends — any future tweaks (button copy, links) take effect next time you copy.</p>
                </div>
              </details>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};



// ────── Availability card wrapper ──────
// Expandable card that LOCKS + collapses while `unlocked === false`.
// Once the user finishes onboarding the chevron + body becomes
// interactive. Click the row to toggle open/closed.
const AvailabilityCardWrapper = ({ api, unlocked }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card data-testid="availability-card-wrapper">
      <button
        type="button"
        onClick={() => unlocked && setOpen((v) => !v)}
        disabled={!unlocked}
        className={`w-full text-left p-4 flex items-center gap-3 ${unlocked ? 'hover:bg-gray-50 dark:hover:bg-[#1a1a1a]' : 'cursor-not-allowed opacity-70'}`}
        data-testid="availability-card-toggle"
      >
        {/* iter163 — Match the Email Signature / Default Call Recording
            layout: leading sage icon + trailing chevron. The lock
            indicator (when locked) replaces the chevron on the right
            so the icon column stays consistent. */}
        <Clock className="w-5 h-5 text-[#586D52]" />
        <div className="flex-1 min-w-0">
          <CardTitle className="text-base">Availability</CardTitle>
          <CardDescription className="text-xs">
            {unlocked
              ? 'Set your work-from / lunch / unavailable hours'
              : 'Available once your onboarding is complete'}
          </CardDescription>
        </div>
        {!unlocked ? (
          <LockIcon className="w-5 h-5 text-amber-500" />
        ) : open ? (
          <ChevronUp className="w-5 h-5" />
        ) : (
          <ChevronDown className="w-5 h-5" />
        )}
      </button>
      {unlocked && open && (
        <div className="px-2 pb-2" data-testid="availability-card-body">
          <AvailabilitySection api={api} />
        </div>
      )}
    </Card>
  );
};


const SettingsPage = () => {
  const { user, api, refreshUser, isFeatureEnabled } = useAuth();
  const { theme, toggleTheme, desktopView, toggleDesktopView } = useTheme();
  const navigate = useNavigate();
  usePageHeader({ title: 'Settings' }, []);
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);

  // MFA States
  const [mfaStatus, setMfaStatus] = useState({
    mfa_enabled: false,
    totp_enabled: false,
    recovery_key_enabled: false
  });
  const [showAuthenticatorModal, setShowAuthenticatorModal] = useState(false);
  const [showRecoveryKeyModal, setShowRecoveryKeyModal] = useState(false);
  const [totpSecret, setTotpSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [showQrCode, setShowQrCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingTotp, setVerifyingTotp] = useState(false);
  const [recoveryKeyStep, setRecoveryKeyStep] = useState('initial');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState('');
  const [showRecoveryKey, setShowRecoveryKey] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  // iter 99 — Recovery-key regen approval flow
  const [pendingRequest, setPendingRequest] = useState(null);     // {id, status, expires_at, decided_by_name, ...}
  const [countdown, setCountdown] = useState('');                  // mm:ss left in approval window
  const [requestingRegen, setRequestingRegen] = useState(false);
  const [rejectInfo, setRejectInfo] = useState(null);              // {reason, decided_by_name}

  // Supervisor call forwarding state
  const [supervisorSettings, setSupervisorSettings] = useState(null);
  const [forwardCallsEnabled, setForwardCallsEnabled] = useState(false);
  const [forwardNumber, setForwardNumber] = useState('');
  const [savingForwardSettings, setSavingForwardSettings] = useState(false);

  // iter162 — Two-credential gate. Each reset (MFA / Password /
  // Recovery) requires the user to enter the OTHER two credentials
  // before the reset modal opens. The gate dialog collects them and
  // calls /me/security/verify-credentials. On success the original
  // reset modal opens.
  //   • Reset MFA      → recovery_key + password
  //   • Reset Password → recovery_key + mfa code
  //   • Reset Recovery → mfa code + password
  const [gateOpen, setGateOpen] = useState(false);
  const [gateTarget, setGateTarget] = useState(null);   // 'mfa' | 'recovery' | 'password'
  const [gatePassword, setGatePassword] = useState('');
  const [gateRecoveryKey, setGateRecoveryKey] = useState('');
  const [gateMfaCode, setGateMfaCode] = useState('');
  const [gateBusy, setGateBusy] = useState(false);
  const [gateError, setGateError] = useState('');
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  // iter163 — Security card collapsible state, same UX as Email
  // Signature / Default Call Recording. Defaults to CLOSED.
  const [securityCardOpen, setSecurityCardOpen] = useState(false);

  const openGate = (target) => {
    // iter167 — Recovery-key rotation should NOT be gated by the
    // iter162 two-credential dialog: the request-regen endpoint
    // already requires password + MFA, and showing the gate first
    // forces the user to enter THREE credentials in two screens.
    // For 'recovery' we open the modal directly and let it run the
    // password → MFA sequence in one place.
    if (target === 'recovery') {
      setShowRecoveryKeyModal(true);
      setRecoveryKeyStep('initial');
      setPassword('');
      setMfaCode('');
      setRecoveryKey('');
      setShowRecoveryKey(false);
      return;
    }
    setGateTarget(target);
    setGatePassword('');
    setGateRecoveryKey('');
    setGateMfaCode('');
    setGateError('');
    setGateOpen(true);
  };

  const submitGate = async () => {
    if (!gateTarget) return;
    setGateBusy(true);
    setGateError('');
    try {
      const body = { target: gateTarget };
      // Send only the fields the target requires so the backend can
      // do strict validation per reset path.
      if (gateTarget === 'mfa' || gateTarget === 'recovery') body.password = gatePassword;
      if (gateTarget === 'mfa' || gateTarget === 'password') body.recovery_key = gateRecoveryKey;
      if (gateTarget === 'password' || gateTarget === 'recovery') body.totp_code = gateMfaCode;
      await api.post('/me/security/verify-credentials', body);
      setGateOpen(false);
      // Hand off to the appropriate flow
      if (gateTarget === 'mfa') {
        await handleSetupAuthenticator();
      } else if (gateTarget === 'recovery') {
        setShowRecoveryKeyModal(true);
        setRecoveryKeyStep('initial');
        setPassword('');
        setRecoveryKey('');
        setShowRecoveryKey(false);
      } else if (gateTarget === 'password') {
        setShowPasswordResetModal(true);
      }
    } catch (err) {
      setGateError(err?.response?.data?.detail || 'Verification failed');
    } finally {
      setGateBusy(false);
    }
  };

  const isSupervisor = user?.role === 'supervisor';
  // iter178 — Several cards (Availability, Email Signature, Call
  // Routing) depend on state assigned during the full onboarding
  // commit, so they are hidden until a user is fully onboarded.
  // Admin / super_admin see them unconditionally.
  const post_onboarded = !!user?.is_onboarded
    || user?.role === 'admin'
    || user?.role === 'super_admin';

  // Fetch MFA status on mount
  useEffect(() => {
    fetchMfaStatus();
    if (isSupervisor) {
      fetchSupervisorSettings();
    }
  }, []);

  const fetchSupervisorSettings = async () => {
    try {
      const res = await api.get('/twilio/supervisor/settings');
      if (res.data.settings) {
        setSupervisorSettings(res.data.settings);
        setForwardCallsEnabled(res.data.settings.forward_calls_to_external || false);
        setForwardNumber(res.data.settings.external_forward_number || '');
      }
    } catch (error) {
      console.error('Failed to fetch supervisor settings:', error);
    }
  };

  const handleSaveSupervisorSettings = async () => {
    setSavingForwardSettings(true);
    try {
      await api.post('/twilio/supervisor/settings', {
        forward_calls_to_external: forwardCallsEnabled,
        external_forward_number: forwardCallsEnabled ? forwardNumber : null,
        receive_unassigned_notifications: true
      });
      toast.success('Call routing settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingForwardSettings(false);
    }
  };

  const fetchMfaStatus = async () => {
    try {
      const response = await api.get('/mfa/status');
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch MFA status:', error);
    }
  };

  // MFA Functions
  const handleSetupAuthenticator = async () => {
    setShowAuthenticatorModal(true);
    try {
      const response = await api.post('/mfa/setup-totp');
      setTotpSecret(response.data.totp_secret);
      setQrCode(response.data.qr_code);
    } catch (error) {
      toast.error('Failed to setup authenticator');
      setShowAuthenticatorModal(false);
    }
  };

  const handleVerifyTotp = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifyingTotp(true);
    try {
      await api.post('/mfa/verify-totp', { code: verificationCode });
      toast.success('Authenticator app enabled!');
      await refreshUser();
      await fetchMfaStatus();
      setShowAuthenticatorModal(false);
      setVerificationCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifyingTotp(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleOpenRecoveryKey = () => {
    setShowRecoveryKeyModal(true);
    setRecoveryKeyStep('initial');
    setPassword('');
    setRecoveryKey('');
    setShowRecoveryKey(false);
  };

  // ── iter 99: Recovery-key approval flow ──────────────────────
  const fetchRecoveryRequest = async () => {
    try {
      const res = await api.get('/mfa/recovery-key/status');
      const req = res.data?.request || null;
      setPendingRequest(req);
      if (res.data?.recovery_key) {
        // One-shot reveal — server has wiped the plaintext.
        setRecoveryKey(res.data.recovery_key);
        setRecoveryKeyStep('display');
        setShowRecoveryKeyModal(true);
        await fetchMfaStatus();
      }
      if (req?.status === 'rejected' && req?.decided_at) {
        setRejectInfo({ reason: req.reject_reason, decided_by_name: req.decided_by_name });
      }
      return req;
    } catch (_e) {
      return null;
    }
  };

  // Countdown ticker — recomputes each second while a request is pending.
  useEffect(() => {
    if (!pendingRequest || pendingRequest.status !== 'pending') {
      setCountdown('');
      return;
    }
    const tick = () => {
      // iter167 — Admin self-rotation has no `expires_at`; the
      // server-authoritative unlock timestamp lives in
      // `auto_approve_at`. Pick whichever applies to the flow.
      const targetIso = pendingRequest.flow === 'admin_self_rotation'
        ? pendingRequest.auto_approve_at
        : pendingRequest.expires_at;
      if (!targetIso) { setCountdown(''); return; }
      const ms = new Date(targetIso).getTime() - Date.now();
      if (ms <= 0) {
        setCountdown('Expired');
        fetchRecoveryRequest();
        return;
      }
      const mins = Math.floor(ms / 60000);
      const secs = Math.floor((ms % 60000) / 1000);
      setCountdown(`${mins}m ${secs.toString().padStart(2, '0')}s`);
    };
    tick();
    const i = setInterval(tick, 1000);
    const p = setInterval(fetchRecoveryRequest, 10000);
    return () => { clearInterval(i); clearInterval(p); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRequest?.id, pendingRequest?.status, pendingRequest?.expires_at, pendingRequest?.auto_approve_at, pendingRequest?.flow]);

  useEffect(() => { fetchRecoveryRequest(); /* eslint-disable-line */ }, []);

  // iter168 — Deep-link from notifications. Three intents:
  //   • `?tab=security`           → expand the Security card.
  //   • `?revealRequest=<id>`     → user got the "ready to reveal"
  //                                 notification; poll status which
  //                                 reveals the plaintext exactly
  //                                 once and opens the modal at
  //                                 `display`.
  //   • `?rotationStatus=<id>`    → user got a rejected / blocked /
  //                                 expired notification; open the
  //                                 modal at the current step so
  //                                 they see the matching screen
  //                                 (no credential replay).
  // Also: if any pending rotation exists, open the modal at the
  // live countdown step automatically when we land on /settings.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const wantSecurity = sp.get('tab') === 'security';
    const reveal = sp.get('revealRequest');
    const status = sp.get('rotationStatus');
    if (wantSecurity || reveal || status) {
      setSecurityCardOpen(true);
    }
    if (reveal) {
      // Force a status poll — server reveals + wipes plaintext.
      fetchRecoveryRequest();
    }
    if (status && pendingRequest && pendingRequest.id === status) {
      if (pendingRequest.status === 'rejected') {
        setRejectInfo({ reason: pendingRequest.reject_reason, decided_by_name: pendingRequest.decided_by_name });
        setRecoveryKeyStep('rejected');
        setShowRecoveryKeyModal(true);
      } else if (pendingRequest.status === 'blocked') {
        setRejectInfo({ reason: `Stopped by ${pendingRequest.blocked_by_name || 'another admin'}.`, decided_by_name: pendingRequest.blocked_by_name });
        setRecoveryKeyStep('rejected');
        setShowRecoveryKeyModal(true);
      }
    }
    // If a pending rotation is live, jump straight to the live
    // countdown — same target the notifications point at.
    if (pendingRequest?.status === 'pending') {
      const step = pendingRequest.flow === 'admin_self_rotation' ? 'admin_cooloff' : 'pending_approval';
      setRecoveryKeyStep(step);
      setShowRecoveryKeyModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRequest?.id, pendingRequest?.status]);

  const handleVerifyPassword = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setVerifyingPassword(true);
    try {
      // iter 99 — first-time activation goes straight to reveal;
      // regeneration of an existing key goes through the supervisor-
      // approval request flow.
      if (mfaStatus.recovery_key_enabled) {
        // iter 120 — regeneration always requires password + MFA code.
        // The backend branches by role: lead/affiliate → notify state
        // supervisors; supervisor (incl. special users) → notify all
        // admins; admin → start a 5-min cool-off with peer-admin
        // restrict-and-logout.
        const res = await api.post('/mfa/recovery-key/request-regen', { password, mfa_code: mfaCode });
        setPendingRequest(res.data);
        const flow = res.data?.flow;
        if (flow === 'admin_self_rotation') {
          setRecoveryKeyStep('admin_cooloff');
          toast.success('Cool-off started. Other admins have 5 minutes to review.');
        } else {
          setRecoveryKeyStep('pending_approval');
          toast.success('Request submitted.');
        }
      } else {
        await api.post('/mfa/verify-password', { password });
        setRecoveryKeyStep('reveal');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid password');
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleCancelRegenRequest = async () => {
    setRequestingRegen(true);
    try {
      await api.post('/mfa/recovery-key/cancel');
      setPendingRequest(null);
      setRecoveryKeyStep('initial');
      toast.success('Request cancelled.');
    } catch (e) {
      toast.error('Could not cancel request');
    } finally {
      setRequestingRegen(false);
    }
  };

  const handleGenerateRecoveryKey = async () => {
    setGeneratingKey(true);
    try {
      // iter 99 — only used for FIRST-TIME activation. Regeneration
      // goes through /mfa/recovery-key/request-regen.
      const response = await api.post('/mfa/generate-recovery-key');
      setRecoveryKey(response.data.recovery_key);
      setRecoveryKeyStep('display');
      await refreshUser();
      await fetchMfaStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate recovery key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const closeRecoveryKeyModal = () => {
    setShowRecoveryKeyModal(false);
    setRecoveryKeyStep('initial');
    setPassword('');
    setRecoveryKey('');
    setShowRecoveryKey(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 space-y-4 pb-24" data-testid="settings-page">
      {/* Welcome Page - Only show if feature is enabled */}
      {isFeatureEnabled('settings_welcome_rewatch') && (
      <Card data-testid="welcome-card">
        <CardHeader>
          <CardTitle className="text-base">Welcome Page</CardTitle>
          <CardDescription>Rewatch the introduction video</CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => setShowWelcomeVideo(true)}
            data-testid="welcome-page-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Watch Welcome Video
          </Button>
        </CardContent>
      </Card>
      )}

      {/* Appearance - Only show if feature is enabled */}
      {isFeatureEnabled('settings_appearance') && (
      <Card data-testid="appearance-card">
        <CardHeader>
          <CardTitle className="text-base">Appearance</CardTitle>
          <CardDescription>Customize how the app looks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Theme toggle — label flips between Dark / Light based on
              the currently active mode. */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              <span>{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
            </div>
            <Switch 
              checked={theme === 'dark'}
              onCheckedChange={toggleTheme}
              data-testid="theme-toggle"
            />
          </div>

          {/* Desktop view toggle — only visible on landscape desktop
              (≥ 768px landscape) since the layout cap is desktop-only.
              Persists per-device via localStorage. */}
          <div className="desktop-view-row hidden items-center justify-between md:landscape:flex">
            <div className="flex items-center gap-2">
              {desktopView === 'collapsed'
                ? <Columns2 className="w-4 h-4" />
                : <Maximize2 className="w-4 h-4" />}
              <span>{desktopView === 'collapsed' ? 'Collapsed View' : 'Expanded View'}</span>
            </div>
            <Switch
              checked={desktopView === 'expanded'}
              onCheckedChange={toggleDesktopView}
              data-testid="desktop-view-toggle"
            />
          </div>
        </CardContent>
      </Card>
      )}

      {/* iter156 — Default Call Recording (admin + special users only).
          Lives directly under Appearance per user spec. Leads, affiliates,
          and supervisors are policy-locked to recording=ON and never see
          this card. */}
      {(user?.role === 'admin' || !!user?.created_by_admin) && (
        <DefaultCallRecordingCard api={api} />
      )}

      {/* iter178 — Hide onboarding-dependent cards (Availability, Email
       *  Signature, Call Routing) until the user has finished the full
       *  onboarding flow. Admin / super_admin bypass the gate so they
       *  can configure defaults at any point.
       *
       *  Rationale: these three cards all depend on data that only
       *  exists AFTER onboarding completes — a verified work email for
       *  the signature, an assigned state line for routing, and the
       *  Twilio pool membership for availability routing. Showing them
       *  mid-onboarding both confuses the user and lets them edit
       *  settings that get overwritten on the final onboarding commit. */}
      {post_onboarded && (
        <>
          {/* Availability — expandable card. */}
          <AvailabilityCardWrapper api={api} unlocked={true} />

          {/* iter133 — Email Signature card. Collapsed by default; on
              expand, fetches the server-rendered HTML and offers a
              1-click "Copy HTML" button so the user can paste their
              live signature into Outlook / Gmail / etc. The HTML is
              generated by the SAME server function (`_build_signature_html`)
              used at email-send time, so any future tweaks to button
              logic (pitch state, caps mode, copy changes) automatically
              flow into pasted-elsewhere signatures. */}
          <EmailSignatureCard api={api} />
        </>
      )}

      {/* Security (iter162 — renamed from "Two-Factor Authentication".
          Now hosts MFA, Recovery Key, AND Password reset entries.)
          iter163 — collapsible card matching Email Signature pattern. */}
      <Card data-testid="mfa-card">
        <button
          type="button"
          onClick={() => setSecurityCardOpen((v) => !v)}
          className="w-full text-left p-4 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]"
          data-testid="security-card-toggle"
        >
          <Shield className="w-5 h-5 text-[#586D52]" />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base">Security</CardTitle>
            <CardDescription className="text-xs">
              Ensure your account is secure
            </CardDescription>
          </div>
          {securityCardOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        {securityCardOpen && (
        <CardContent className="space-y-3 pt-0" data-testid="security-card-body">
          {/* Two-Factor Authenticator (renamed from "Authenticator App") */}
          <div
            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => openGate('mfa')}
            data-testid="settings-authenticator-option"
          >
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Two-Factor Authenticator</p>
                <p className="text-xs text-muted-foreground">Click to Reset</p>
              </div>
            </div>
            {mfaStatus.totp_enabled ? (
              <Badge variant="outline" className="text-green-600 border-green-600">Enabled</Badge>
            ) : (
              <Badge variant="outline">Setup</Badge>
            )}
          </div>

          {/* Recovery Key */}
          <div
            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => {
              // iter167 — If there's already a pending rotation,
              // tapping the row should jump STRAIGHT to the live
              // countdown (admin cool-off OR approval-pending),
              // never replay the credential gate or the
              // "Activate Recovery Key" wizard from scratch.
              if (pendingRequest?.status === 'pending') {
                const step = pendingRequest.flow === 'admin_self_rotation'
                  ? 'admin_cooloff'
                  : 'pending_approval';
                setRecoveryKeyStep(step);
                setShowRecoveryKeyModal(true);
                return;
              }
              openGate('recovery');
            }}
            data-testid="settings-recovery-key-option"
          >
            <div className="flex items-center gap-3">
              <Key className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Recovery Key</p>
                <p className="text-xs text-muted-foreground">Click to Reset</p>
              </div>
            </div>
            {mfaStatus.recovery_key_enabled ? (
              <Badge variant="outline" className="text-green-600 border-green-600">Enabled</Badge>
            ) : (
              <Badge variant="outline">Setup</Badge>
            )}
          </div>

          {/* iter162 — Password entry. Same visual treatment as the
              two tabs above so the Security card is a single tidy
              "everything you need to keep your account locked down"
              panel. Tap to open the password rotate flow. */}
          <div
            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => openGate('password')}
            data-testid="settings-password-option"
          >
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Password</p>
                <p className="text-xs text-muted-foreground">Click to Reset</p>
              </div>
            </div>
            {/* iter163 — Every authenticated session has a password
                by definition (the user just used it to sign in). So
                this badge is permanently "Enabled" for both new and
                legacy/demo accounts, matching the visual parity of
                the two siblings above. */}
            <Badge variant="outline" className="text-green-600 border-green-600">Enabled</Badge>
          </div>

          {/* iter 99 — pending-approval banner (visible on Settings load) */}
          {pendingRequest?.status === 'pending' && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/15 dark:border-amber-800 p-3 mt-3" data-testid="recovery-key-pending-banner">
              <div className="flex items-start gap-2">
                <Loader2 className="w-4 h-4 text-amber-700 animate-spin flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                    {/* iter167 — wording matches the actual approval
                        pool: admin self-rotation has no approval at
                        all (it's a peer-review cool-off). */}
                    {pendingRequest.flow === 'admin_self_rotation'
                      ? 'Recovery key rotation cool-off in progress'
                      : (user?.role === 'supervisor' || user?.created_by_admin)
                        ? 'Recovery key regen pending admin approval'
                        : 'Recovery key regen pending supervisor approval'}
                  </p>
                  <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                    Time remaining: <span className="font-mono tabular-nums">{countdown || '—'}</span>
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => {
                    // iter167 — open the modal on the step that
                    // matches the live request flow (cool-off vs
                    // approval pending). Never replay credentials.
                    const step = pendingRequest.flow === 'admin_self_rotation'
                      ? 'admin_cooloff'
                      : 'pending_approval';
                    setRecoveryKeyStep(step);
                    setShowRecoveryKeyModal(true);
                  }}
                  data-testid="recovery-key-pending-banner-view-btn"
                >
                  View
                </Button>
              </div>
            </div>
          )}

          {/* Status Message */}
          {mfaStatus.totp_enabled && mfaStatus.recovery_key_enabled ? (
            <p className="text-xs text-green-600 text-center pt-2">
              ✓ Both MFA methods enabled - maximum security
            </p>
          ) : (
            <p className="text-xs text-muted-foreground text-center pt-2">
              Enable both methods for maximum security
            </p>
          )}
        </CardContent>
        )}
      </Card>

      {/* Supervisor Call Routing (only for supervisors who have
          finished onboarding — admin bypass applies). Pre-onboarded
          supervisors don't have a state line assigned yet, so this
          card would be editing routing on a number that doesn't
          exist. */}
      {isSupervisor && post_onboarded && (
      <Card data-testid="supervisor-call-routing-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <PhoneForwarded className="w-5 h-5 text-blue-600" />
            Call Routing
          </CardTitle>
          <CardDescription>
            Choose how you receive calls from the state number
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Forward to external number</p>
              <p className="text-xs text-muted-foreground">
                {forwardCallsEnabled 
                  ? 'Calls will forward to your Teams/external number' 
                  : 'Calls will ring in The Uplift app'}
              </p>
            </div>
            <Switch 
              checked={forwardCallsEnabled}
              onCheckedChange={setForwardCallsEnabled}
              data-testid="forward-calls-toggle"
            />
          </div>

          {forwardCallsEnabled && (
            <div className="space-y-2">
              <Label className="text-xs">Forwarding Number</Label>
              <Input
                placeholder="+1 (555) 123-4567"
                value={forwardNumber}
                onChange={(e) => setForwardNumber(e.target.value)}
                data-testid="forward-number-input"
              />
              <p className="text-[10px] text-muted-foreground">
                Enter the number where calls should be forwarded (e.g., your Teams number)
              </p>
            </div>
          )}

          <Button 
            className="w-full bg-[#586D52] hover:bg-[#4a5d46]"
            onClick={handleSaveSupervisorSettings}
            disabled={savingForwardSettings}
            data-testid="save-call-routing-btn"
          >
            {savingForwardSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Call Routing
          </Button>
        </CardContent>
      </Card>
      )}

      {/* Sign Out moved to /profile (iter140) — see ProfilePage's
          bottom Sign Out button. Keeping a single source of truth
          for log-out keeps the action discoverable in one place. */}

      {/* Welcome Video Modal */}
      {showWelcomeVideo && (
        <WelcomeVideoPlayer onClose={() => setShowWelcomeVideo(false)} />
      )}

      {/* Authenticator App Modal */}
      <Dialog open={showAuthenticatorModal} onOpenChange={setShowAuthenticatorModal}>
        <DialogContent className="max-w-sm mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Setup Authenticator App</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <Label className="text-sm text-muted-foreground">
                Enter this secret key in your authenticator app:
              </Label>
              <div className="mt-2 bg-background rounded-lg p-3 text-center overflow-x-auto">
                <code className="text-sm font-mono tracking-wide break-all">
                  {totpSecret}
                </code>
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-2"
                onClick={() => copyToClipboard(totpSecret, 'Secret key')}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Key
              </Button>
              <button 
                onClick={() => setShowQrCode(!showQrCode)}
                className="w-full text-center mt-3 text-sm text-primary hover:underline"
              >
                {showQrCode ? 'Hide QR Code' : 'See QR Code'}
              </button>
              {showQrCode && qrCode && (
                <div className="mt-3 flex justify-center">
                  <img src={qrCode} alt="QR Code" className="w-32 h-32 rounded-lg" />
                </div>
              )}
            </div>
            <div className="bg-muted/30 rounded-lg p-4">
              <Label className="text-sm text-muted-foreground">
                Enter the 6-digit code from your app:
              </Label>
              <Input
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="mt-2 text-center text-xl tracking-widest font-mono"
                maxLength={6}
              />
            </div>
            <Button 
              className="w-full"
              onClick={handleVerifyTotp}
              disabled={verifyingTotp || verificationCode.length !== 6}
            >
              {verifyingTotp ? 'Verifying...' : 'Enable Authenticator'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recovery Key Modal */}
      <Dialog open={showRecoveryKeyModal} onOpenChange={closeRecoveryKeyModal}>
        <DialogContent className="max-w-sm mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {mfaStatus.recovery_key_enabled ? 'Regenerate Recovery Key' : 'Setup Recovery Key'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {recoveryKeyStep === 'initial' && (
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm mb-2">
                  Generate a unique Recovery Key. <span className="font-semibold">NOTE:</span>
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Store it in a secure location!</li>
                  <li>• Recovery Keys can be regenerated if lost.</li>
                </ul>
                <Button 
                  className="w-full mt-4"
                  onClick={() => setRecoveryKeyStep('password')}
                >
                  <Key className="w-4 h-4 mr-2" />
                  {mfaStatus.recovery_key_enabled ? 'Regenerate Key' : 'Activate Recovery Key'}
                </Button>
              </div>
            )}

            {recoveryKeyStep === 'password' && (
              <div className="bg-muted/30 rounded-lg p-4">
                <Label className="text-sm text-muted-foreground">
                  Enter your password to continue:
                </Label>
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2"
                  data-testid="recovery-key-password-input"
                  autoComplete="current-password"
                />
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => { setRecoveryKeyStep('initial'); setPassword(''); setMfaCode(''); }}
                  >
                    Back
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => {
                      if (!password) { toast.error('Please enter your password'); return; }
                      // First-time activation has no MFA gate — just
                      // reveal. Rotations need the MFA step next.
                      if (mfaStatus.recovery_key_enabled) {
                        setRecoveryKeyStep('mfa');
                      } else {
                        handleVerifyPassword();
                      }
                    }}
                    disabled={!password || verifyingPassword}
                    data-testid="recovery-key-password-next-btn"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {recoveryKeyStep === 'mfa' && (
              <div className="bg-muted/30 rounded-lg p-4">
                <Label className="text-sm text-muted-foreground">
                  Enter your 6-digit authenticator code <span className="text-xs">(or current recovery key)</span>:
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  className="mt-2"
                  data-testid="recovery-key-mfa-input"
                />
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => { setRecoveryKeyStep('password'); setMfaCode(''); }}
                  >
                    Back
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleVerifyPassword}
                    disabled={verifyingPassword || !mfaCode}
                    data-testid="recovery-key-verify-btn"
                  >
                    {verifyingPassword ? 'Verifying...' : 'Verify'}
                  </Button>
                </div>
              </div>
            )}

            {recoveryKeyStep === 'admin_cooloff' && pendingRequest && (
              <AdminCooloffPanel
                request={pendingRequest}
                api={api}
                onCleared={() => {
                  setPendingRequest(null);
                  setRecoveryKeyStep('initial');
                  setPassword(''); setMfaCode('');
                  fetchMfaStatus();
                }}
                onRevealReady={(plaintext) => {
                  setRecoveryKey(plaintext);
                  setRecoveryKeyStep('display');
                  setPendingRequest(null);
                }}
              />
            )}

            {recoveryKeyStep === 'pending_approval' && pendingRequest && (
              // iter167 — Admin self-rotation renders the live cool-off
              // panel here too so navigating back into the modal during
              // a pending rotation lands on the SAME countdown, not on
              // "supervisor approval" copy that doesn't apply.
              pendingRequest.flow === 'admin_self_rotation' ? (
                <AdminCooloffPanel
                  request={pendingRequest}
                  api={api}
                  onCleared={() => {
                    setPendingRequest(null);
                    setRecoveryKeyStep('initial');
                    setPassword(''); setMfaCode('');
                    fetchMfaStatus();
                  }}
                  onRevealReady={(plaintext) => {
                    setRecoveryKey(plaintext);
                    setRecoveryKeyStep('display');
                    setPendingRequest(null);
                  }}
                />
              ) : (
              <div className="bg-muted/30 rounded-lg p-4 space-y-3" data-testid="recovery-key-pending">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
                  <p className="font-semibold">
                    {/* iter167 — supervisors/special users are
                        approved by ADMINS, not supervisors. Only
                        leads / affiliates wait on a supervisor. */}
                    {(user?.role === 'supervisor' || user?.created_by_admin)
                      ? 'Awaiting admin approval'
                      : 'Awaiting supervisor approval'}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {(user?.role === 'supervisor' || user?.created_by_admin)
                    ? 'Your admins have been notified and have 1 hour to approve or decline this request.'
                    : 'Your supervisor has been notified and has 1 hour to approve or decline this request.'}
                </p>
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Time remaining</p>
                  <p className="text-2xl font-mono font-semibold text-amber-700 dark:text-amber-300 tabular-nums" data-testid="recovery-key-countdown">
                    {countdown || '—'}
                  </p>
                </div>
                {pendingRequest.approver_count != null && (
                  <p className="text-xs text-muted-foreground">
                    Notified {pendingRequest.approver_count} {(user?.role === 'supervisor' || user?.created_by_admin) ? 'admin' : 'supervisor'}{pendingRequest.approver_count === 1 ? '' : 's'}.
                  </p>
                )}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleCancelRegenRequest}
                  disabled={requestingRegen}
                  data-testid="recovery-key-cancel-request-btn"
                >
                  Cancel Request
                </Button>
              </div>
              )
            )}

            {recoveryKeyStep === 'rejected' && (
              <div className="bg-muted/30 rounded-lg p-4 space-y-3" data-testid="recovery-key-rejected">
                <div className="flex items-center gap-2">
                  <X className="w-5 h-5 text-red-600" />
                  <p className="font-semibold">Request declined</p>
                </div>
                {rejectInfo?.decided_by_name && (
                  <p className="text-sm text-muted-foreground">
                    Declined by <span className="font-medium">{rejectInfo.decided_by_name}</span>.
                  </p>
                )}
                {rejectInfo?.reason && (
                  <p className="text-sm bg-red-50 dark:bg-red-900/20 rounded-md p-2 border border-red-200 dark:border-red-800">
                    "{rejectInfo.reason}"
                  </p>
                )}
                <Button className="w-full" onClick={closeRecoveryKeyModal}>Close</Button>
              </div>
            )}

            {recoveryKeyStep === 'reveal' && (
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="font-semibold">Password verified.</p>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Click below to reveal your Recovery Key.
                </p>
                <Button 
                  className="w-full"
                  onClick={handleGenerateRecoveryKey}
                  disabled={generatingKey}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {generatingKey ? 'Generating...' : 'Reveal Recovery Key'}
                </Button>
              </div>
            )}

            {recoveryKeyStep === 'display' && (
              <div className="bg-muted/30 rounded-lg p-4">
                <Label className="text-sm text-muted-foreground">
                  Your 12-digit Recovery Key:
                </Label>
                <div className="mt-2 bg-background rounded-lg p-3 text-center relative">
                  {showRecoveryKey ? (
                    <code className="text-lg font-mono tracking-wider">
                      {recoveryKey}
                    </code>
                  ) : (
                    <code className="text-lg font-mono tracking-wider blur-sm select-none">
                      {recoveryKey}
                    </code>
                  )}
                </div>
                <div className="flex items-start gap-2 mt-3 p-2 bg-yellow-500/10 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    Store this code securely. You'll need it if you lose access to your authenticator.
                  </p>
                </div>
                <Button 
                  variant="outline"
                  className="w-full mt-3"
                  onClick={() => setShowRecoveryKey(!showRecoveryKey)}
                >
                  {showRecoveryKey ? (
                    <><EyeOff className="w-4 h-4 mr-2" />Hide Code</>
                  ) : (
                    <><Eye className="w-4 h-4 mr-2" />Show Code</>
                  )}
                </Button>
                <Button 
                  className="w-full mt-2"
                  onClick={() => copyToClipboard(recoveryKey, 'Recovery key')}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Key
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* iter162 — Two-credential GATE dialog. Opens before any of
          the three reset flows. Each reset requires the OTHER two
          credentials:
            • Reset MFA      → recovery_key + password
            • Reset Password → recovery_key + 6-digit code
            • Reset Recovery → 6-digit code + password
          Server endpoint /me/security/verify-credentials is the
          authoritative check (rate-limited + brute-force counted). */}
      <Dialog open={gateOpen} onOpenChange={(v) => !gateBusy && setGateOpen(v)}>
        <DialogContent className="max-w-sm mx-auto" data-testid="security-gate-dialog">
          <DialogHeader>
            <DialogTitle>
              {gateTarget === 'mfa' && 'Confirm to reset MFA'}
              {gateTarget === 'recovery' && 'Confirm to reset Recovery Key'}
              {gateTarget === 'password' && 'Confirm to reset Password'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              For your security, enter your other two credentials before resetting.
            </p>
            {(gateTarget === 'mfa' || gateTarget === 'password') && (
              <div>
                <Label htmlFor="gate-recovery" className="text-xs">Recovery Key</Label>
                <Input
                  id="gate-recovery"
                  type="text"
                  value={gateRecoveryKey}
                  onChange={(e) => setGateRecoveryKey(e.target.value)}
                  placeholder="12-digit recovery key"
                  className="mt-1 font-mono"
                  data-testid="gate-recovery-input"
                  autoComplete="off"
                />
              </div>
            )}
            {(gateTarget === 'password' || gateTarget === 'recovery') && (
              <div>
                <Label htmlFor="gate-mfa" className="text-xs">Authenticator Code</Label>
                <Input
                  id="gate-mfa"
                  type="text"
                  inputMode="numeric"
                  value={gateMfaCode}
                  onChange={(e) => setGateMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="mt-1 text-center text-lg tracking-widest font-mono"
                  data-testid="gate-mfa-input"
                  autoComplete="off"
                />
              </div>
            )}
            {(gateTarget === 'mfa' || gateTarget === 'recovery') && (
              <div>
                <Label htmlFor="gate-password" className="text-xs">Current Password</Label>
                <Input
                  id="gate-password"
                  type="password"
                  value={gatePassword}
                  onChange={(e) => setGatePassword(e.target.value)}
                  placeholder="Enter your password"
                  className="mt-1"
                  data-testid="gate-password-input"
                  autoComplete="current-password"
                />
              </div>
            )}
            {gateError && (
              <p className="text-xs text-red-600" data-testid="gate-error">{gateError}</p>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setGateOpen(false)} disabled={gateBusy}>
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={submitGate}
                disabled={gateBusy}
                data-testid="gate-submit-btn"
              >
                {gateBusy ? 'Verifying…' : 'Continue'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter162 — Password Reset modal. Reached only after the gate
          dialog above succeeds (so the user has already proven
          recovery_key + TOTP). Submits a single new-password PUT
          which the backend pairs with the gate's short-lived
          verification ticket. */}
      <Dialog open={showPasswordResetModal} onOpenChange={setShowPasswordResetModal}>
        <DialogContent className="max-w-sm mx-auto" data-testid="password-reset-modal">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <PasswordResetBody
            api={api}
            onSuccess={() => {
              setShowPasswordResetModal(false);
              toast.success('Password updated');
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// iter162 — Inline component for the password reset body. Keeps
// the SettingsPage outer component tidy + lets us isolate the
// local state for the new password fields without leaking them
// up to SettingsPage's hook tree.
const PasswordResetBody = ({ api, onSuccess }) => {
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!pw || !confirm) { setErr('Both fields are required.'); return; }
    if (pw !== confirm) { setErr('Passwords do not match.'); return; }
    if (pw.length < 12) { setErr('Minimum 12 characters.'); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/me/security/reset-password-after-gate', {
        new_password: pw, confirm_password: confirm,
      });
      onSuccess?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not update password');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="pwr-new" className="text-xs">New Password</Label>
        <Input id="pwr-new" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" data-testid="pwr-new-input" autoComplete="new-password" />
      </div>
      <div>
        <Label htmlFor="pwr-confirm" className="text-xs">Confirm Password</Label>
        <Input id="pwr-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" data-testid="pwr-confirm-input" autoComplete="new-password" />
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
      <Button className="w-full" onClick={submit} disabled={busy} data-testid="pwr-submit-btn">
        {busy ? 'Saving…' : 'Save Password'}
      </Button>
    </div>
  );
};

export default SettingsPage;
